import { DocumentType } from './documents';
import { CarRental, GenerableDocument } from './rental';

export enum AccidentStatus {
  Created = 'created',
  InProgress = 'inProgress',
  WaitingOnDocuments = 'waitingOnDocuments',
  Finished = 'finished'
}

export enum AccidentUserType {
  Person = 'person',
  Company = 'company',
  BusinessMan = 'businessman'
}

export enum ACCIDENT_FILES {
  POWER_OF_ATTORNEY = 'powerOfAttorney',
  ADDITIONAL_INFO = 'accidentAdditionalInfo',
  LEAFLET = 'accidentLeaflet',
  GDPR = 'accidentGdpr'
}

export enum ACCIDENT_SOURCE {
  TIPPER = 'tip',
  SERVICE = 'service',
  INSURANCE = 'insurance'
}

export const AccidentUserTypeValues: Record<AccidentUserType, number> = {
  [AccidentUserType.Person]: 1,
  [AccidentUserType.Company]: 2,
  [AccidentUserType.BusinessMan]: 3
};

export enum RegistryType {
  CAR_MODELS = 'carModels',
  ACCIDENTS_SOURCES = 'accidentSources',
  ACCIDENT_REJECTIONS = 'accidentRejections',
  INSURANCES = 'insuranceCompanies',
  BRANCH_LIST = 'branchList',
  CAR_RENTAL_STATUSES = 'carRentalCarStatuses',
  APP_USER_LIST = 'appUserList',
  INSURANCE_TYPES = 'insuranceTypes',
  STATEMENT_TYPES = 'dealerStatementTypes',
  SERVICES = 'serviceTypes',
  COUNTRY_LIST = 'countryList',
  ACCOUNTING_CENTERS = 'accountingCentres',
  ACCOUNTING_DOCUMENTS = 'dealerAccountingDocumentTypes',
  ACCIDENT_STATUSES = 'accidentStatuses'
}

export enum PayoutType {
  BANK_ACCOUNT = 'bankAccount',
  ADDRESS = 'address',
  INVOICE = 'invoice'
}

export enum TotalCrashType {
  NOT_TOTAL_CRASH = 0,
  IS_TOTAL_CRASH = 1,
  MAYBE_TOTAL_CRASH = 2
}

export enum TenantType {
  OWNER = 'owner',
  CONTACT_PERSON = 'contactPerson'
}

export interface Car {
  id: number;
  brand: string;
  model: string;
  color?: string;
  vin: string;
  plateNumber?: string;
  licenseId?: string;
  manufacturedYear?: string;
  speedometerValue?: string;
  tankValue?: string;
  stk?: string;
  dailyRate?: string;
  wasDamagedBefore?: number;
  hasLeasing?: number;
  leasingNumber?: string;
  isTotalCrash?: TotalCrashType;
  location?: string;
  contactPerson?: string;
}

export interface Company {
  companyName: string;
  companyIdNum: string;
  companyTaxNum?: string;
  contactDegree?: string;
  contactName?: string;
  contactSurname?: string;
  companyVatPayer?: boolean;
}

interface AccidentStatusGroup {
  isCompleted: boolean;
  missing?: string[];
  progress: AccidentStatusProgress;
}

export interface AccidentStatusProgress {
  isCompleted?: boolean;
  groups: {
    accident: AccidentStatusGroup;
    user: AccidentStatusGroup;
    driver: AccidentStatusGroup;
    car: AccidentStatusGroup;
    mediaFiles: AccidentStatusGroup;
    contactPerson: AccidentStatusGroup;
  };
}

export interface Status {
  id: number;
  name: AccidentStatus;
  label: string;
  progress: AccidentStatusProgress;
  color: string;
}

export interface Address {
  street: string;
  city: string;
  zip: string;
  houseNum: string;
  country?: {
    id: number;
    name: string;
  };
  countryId?: number;
}

export interface AccidentUser {
  equalTo?: 'none' | 'user';
  id?: number;
  degree?: string;
  name?: string;
  surname?: string;
  phone?: string;
  email?: string;
  birthDate?: string;
  birthNumber?: string;
  alias?: string;
  type?: {
    name: AccidentUserType;
    label: string;
  };
  account?: any;
  address?: Address;
  company?: Company;
}

export interface AccidentUserRequest {
  type: number;
  id?: number;
  degree?: string;
  name?: string;
  surname?: string;
  birthDate?: string;
  birthNumber?: string;
  street?: string;
  houseNum?: string;
  city?: string;
  zip?: string;
  countryId?: number;
  companyName?: string;
  companyIdNum?: string;
  companyTaxNum?: string;
  companyVatPayer?: boolean;
}

export interface AccidentOperatorRequest {
  equalTo?: 'none' | 'user';
  type?: number;
  id?: number;
  degree?: string;
  name?: string;
  surname?: string;
  phone?: string;
  birthDate?: string;
  birthNumber?: string;
  street?: string;
  houseNum?: string;
  city?: string;
  zip?: string;
  countryId?: number;
  companyName?: string;
  companyIdNum?: string;
  companyTaxNum?: string;
  companyVatPayer?: boolean;
}

export interface Driver {
  equalTo?: 'none' | 'user' | 'operator';
  degree?: string;
  name?: string;
  surname?: string;
  birthDate?: string;
  birthNumber?: string;
  phone?: string;
}

export interface ContactPerson {
  equalTo?: 'none' | 'user' | 'operator' | 'driver';
  degree?: string;
  name?: string;
  surname?: string;
  phone?: string;
  birthNumber?: string;
}

export interface Account {
  name: string;
  surname: string;
  email: string;
  phone?: string;
}

export interface MediaValue {
  id: number;
  url: string;
  webP?: string;
  fileType: DocumentType;
  createdDate: string;
  fileName: string;
}

export interface AccidentMediaItem {
  name: string;
  limit: number;
  label: string;
  values?: MediaValue | MediaValue[];
  required: boolean;
  note?: string;
}

export interface AccidentInvoiceDocument {
  type: string;
  number: string;
  filename: string;
  path: string;
}

export interface AccidentGeneral {
  dealerId?: number;
  isSpotSolved?: boolean;
  isRejected?: boolean;
  rejectionType?: string;
  rejectionReason?: string;
  date: string;
  source?: {
    id?: number;
    type?: string;
    name?: string;
    tradeName?: string;
    address?: string;
    phone?: string;
    bankAccountNumber?: string;
    birthDate?: Date;
  };
}

export interface AccidentSource {
  id: number;
  type: string;
  name: string;
  tradeName?: string | null;
  address?: string;
  commissionValue?: string;
}

export interface Notification {
  email?: string;
  phone?: string;
}

export interface AccidentLabel {
  type: string;
  label: string;
}

export interface Accident {
  id: number;
  contractNumber: string;
  date: string;
  place: string;
  car: Car;
  status: Status;
  labels?: AccidentLabel[];
  user: AccidentUser;
  operator?: AccidentUser;
  carRental?: CarRental[];
  driver?: Driver;
  contactPerson?: ContactPerson;
  isPoliceHandled?: number;
  hasParking?: boolean;
  hasTowing?: boolean;
  hasPhotos?: boolean;
  payoutType?: PayoutType;
  payoutInfo?: string;
  paymentStatus?: {
    name: string;
    label: string;
  };
  insuranceType?: string;
  insuranceCode?: string;
  note?: string;
  carDamageNote?: string;
  internalNote?: string;
  media?: {
    files?: AccidentMediaItem[];
    images?: AccidentMediaItem[];
    serviceFiles?: AccidentMediaItem[];
    communicationFiles?: AccidentMediaItem[];
  };
  documents?: GenerableDocument[];
  insurance: {
    id: number;
    title: string;
    email: string;
  };
  dealerCommission?: number;
  accidentReporting: RegistryTypeLabel;
  source: AccidentSource;
  accountingDocuments: AccidentInvoiceDocument[];
  notification: Notification;
}

export interface AccidentsResponse {
  items?: Accident[];
  pagination: {
    limit: number;
    offset: number;
    pages: number;
    total: number;
  };
}

interface AccidentEntity {
  date?: string;
  place?: string;
  dealerId?: number;
  branchId?: number;
  insuranceId?: number;
  note?: string;
  internalNote?: string;
  insuranceType?: number;
  isPoliceHandled?: number;
  hasParking?: boolean;
  hasTowing?: boolean;
  hasPhotos?: boolean;
  payoutType?: string;
  payoutInfo?: string;
  accidentReporting: string;
}

export interface AccidentRequest {
  source?: AccidentSource;
  accident?: AccidentEntity;
  mediaGroups?: Record<string, string>;
  user?: AccidentUserRequest;
  operator?: AccidentOperatorRequest;
  driver?: Driver;
  contactPerson?: ContactPerson;
  address?: Address;
  company?: Company;
  car?: Car;
  carRental?: CarRental;
  notification?: {
    email: string;
    phone: string;
  };
  account?: Account;
}

/* ACCIDENT FILES */
export interface UploadDocumentRequest {
  accidentId: number;
  files: {
    filename: string;
    data: string;
    template: string;
  }[];
}

export interface CarModelsList {
  title: string;
  models: {
    title: string;
  }[];
}

export interface RegistryTypeLabel {
  type: string;
  label: string;
}

export interface RegistryIdName {
  id: number;
  name: string;
}

export interface RegistryCodeNote {
  code: string;
  note: string;
}

export interface RegistryNameLabel {
  name: string;
  label: string;
}

export interface RegistryIdNameLabel {
  id: number;
  name: string;
  label: string;
}

export interface AccidentSourceValueItem {
  id: number;
  type: string;
  name: string;
  personName?: string;
  displayName?: string;
  address?: string;
  tradeName?: string | null;
  commissionValue?: string;
  bankAccountNumber?: string;
  birthDate?: string;
  phone?: string;
  favourite?: boolean;
  defaultCommissionPaymentType?: 'cash' | 'online' | '';
}

export interface AccidentSourcesList extends RegistryTypeLabel {
  values: AccidentSourceValueItem[];
}

export interface AccidentInsuranceList {
  id: number;
  title: string;
  email?: string;
}

export interface AresResponse {
  code: number;
  response: {
    ico: string;
    dic: string;
    obchodniJmeno: string;
    popis?: string;
    sidlo: {
      nazevMestskeCastiObvodu?: string;
      nazevObce: string;
      nazevUlice: string;
      cisloDomovni: string;
      cisloOrientacni: string;
      psc: string;
    };
    seznamRegistraci: {
      stavZdrojeDph: 'NEEXISTUJICI' | 'AKTIVNI';
    };
  };
}

export interface VehicleResponse {
  code: number;
  response: VehicleData;
}

export interface VehicleData {
  DatumPrvniRegistrace: string;
  DatumPrvniRegistraceVCr: string;
  CisloTypovehoSchvaleni: string;
  HomologaceEs: string;
  VozidloDruh: string;
  VozidloDruh2: string;
  Kategorie: string;
  TovarniZnacka: string;
  Typ: string;
  Varianta: string;
  Verze: string;
  VIN: string;
  ObchodniOznaceni: string;
  VozidloVyrobce: string;
  MotorVyrobce: string;
  MotorTyp: string;
  MotorMaxVykon: string;
  Palivo: string;
  MotorZdvihObjem: number;
  VozidloElektricke: string;
  VozidloHybridni: string;
  VozidloHybridniTrida: string | null;
  EmiseEHKOSNEHSES: string;
  EmisniUroven: string;
  EmiseKSA: string | null;
  EmiseCO2: string;
  EmiseCO2Specificke: string | null;
  EmiseSnizeniNedc: string | null;
  EmiseSnizeniWltp: string | null;
  SpotrebaMetodika: string;
  SpotrebaNa100Km: string;
  Spotreba: string | null;
  SpotrebaEl: string | null;
  DojezdZR: string | null;
  VyrobceKaroserie: string | null;
  KaroserieDruh: string | null;
  KaroserieVyrobniCislo: string | null;
  VozidloKaroserieBarva: string;
  VozidloKaroserieBarvaDoplnkova: string | null;
  VozidloKaroserieMist: string;
  Rozmery: string;
  RozmeryRozvor: string;
  Rozchod: string;
  HmotnostiProvozni: number;
  HmotnostiPripPov: string;
  HmotnostiPripPovN: string;
  HmotnostiPripPovBrzdenePV: string;
  HmotnostiPripPovNebrzdenePV: string;
  HmotnostiPripPovJS: string;
  HmotnostiTestWltp: string | null;
  HmotnostUzitecneZatizeniPrumer: string | null;
  VozidloSpojZarizNazev: string | null;
  NapravyPocetDruh: string;
  NapravyPneuRafky: string;
  HlukStojiciOtacky: string;
  HlukJizda: number;
  NejvyssiRychlost: number;
  PomerVykonHmotnost: string | null;
  InovativniTechnologie: string | null;
  StupenDokonceni: string | null;
  FaktorOdchylkyDe: string | null;
  FaktorVerifikaceVf: string | null;
  VozidloUcel: string | null;
  DalsiZaznamy: string;
  AlternativniProvedeni: string | null;
  CisloTp: string;
  CisloOrv: string;
  OrvZadrzeno: boolean;
  OrvKeSkartaci: string | null;
  OrvOdevzdano: string | null;
  RzDruh: string;
  RzJkVydana: string | null;
  RzKeSkartaci: string | null;
  RzOdevzdano: string | null;
  RzZadrzena: boolean;
  ZarazeniVozidla: string;
  PravidelnaTechnickaProhlidkaDo: string;
  PredRegistraciProhlidkaDne: string | null;
  PredSchvalenimProhlidkaDne: string | null;
  EvidencniProhlidkaDne: string;
  HistorickeVozidloProhlidkaDne: string | null;
  StatusNazev: string;
  PocetVlastniku: number;
  PocetProvozovatelu: number;
}

export interface PhotoNoteType {
  photoName: string;
  note: string;
}

export interface OpenServices {
  id: number;
  sourceId: number;
  carId: number;
  isInternalCar: boolean;
  accidentId: number;
  userId: number;
  assignedTeam: string;
  note?: string;
  serviceInfo?: string;
  sourceName: string;
  isFinished: boolean;
  createdDate: string;
  /** Relativní cesta k uloženému PDF zakázkového listu, `null` = ještě nevygenerován. */
  orderForm?: string | null;
}

export interface ServiceOrderFormFile {
  id: number;
  filename: string;
  /** base64 PDF */
  file: string;
}

export interface ServiceOrderFormResult {
  id: number;
  filename: string;
  orderForm: string;
}

export interface AccidentServiceRequest {
  sourceId: number;
  note?: string;
  serviceInfo?: string;
}

export interface AccidentExistingUserResponse extends Omit<AccidentUser, 'address' | 'company'>, Company {
  street: string;
  houseNum: string;
  city: string;
  zip: string;
  countryId: number;
}

export interface UserEmailAccountResponse {
  id: number;
  objectType: string;
  email: string;
  isActivated: boolean;
}

export interface AddNewAccountRequest {
  name: string;
  surname: string;
  email: string;
  phone?: string;
  degree?: string;
}

export interface AccidentStatusResponse {
  id: number;
  name: string;
  color: string;
  label: string;
}
